import { redirect } from '@tanstack/react-router';

import { ROUTE_FEATURE_FLAGS } from '@/config/route-feature-flags';
import { DEFAULT_FEATURE } from '@/utils/routing';

import { identifyUserInLaunchDarkly } from './shared-utils';
import type { RouteGuardContext, RouteGuardParams } from './types';

/**
 * Creates a route guard for Gaia-only features that don't exist in Darkwing.
 * When the feature flag (looked up via ROUTE_FEATURE_FLAGS) is off, the user
 * is redirected to the facility's default feature route instead of Darkwing.
 *
 * Use `createMigrationRouteGuard` instead when the route has a Darkwing counterpart.
 *
 * @param routeId - The route identifier (must have an entry in ROUTE_FEATURE_FLAGS)
 */
export function createFeatureFlagGuard(routeId: string) {
  return async ({ context, params }: { context: RouteGuardContext; params: RouteGuardParams }) => {
    const { ldClient, auth } = context;
    const flagKey = ROUTE_FEATURE_FLAGS[routeId];

    if (!flagKey) {
      console.warn(`[${routeId} Route] No feature flag configured, allowing navigation`);
      return;
    }

    if (ldClient && auth) {
      await identifyUserInLaunchDarkly(ldClient, auth, params, `${routeId} Route`);
    }

    let flagValue = false;
    if (ldClient) {
      try {
        flagValue = ldClient.variation(flagKey, false);
      } catch (error) {
        console.error(`[${routeId} Route] Error evaluating flag:`, error);
      }
    }

    if (!flagValue) {
      throw redirect({
        to: `/$org/$agent/${DEFAULT_FEATURE}`,
        params: { org: params.org, agent: params.agent },
      });
    }
  };
}
